import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Search, Plus, ExternalLink, FileText, Trash2, RefreshCw, Eye, EyeOff, Pencil, Copy,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  published: { label: "Published", cls: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]" },
  draft: { label: "Draft", cls: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]" },
};

export default function AdminBlogIndex() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ posts: [], total: 0, categories: [] });
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const q = params.get("q") || "";
  const status = params.get("status") || "all";
  const category = params.get("category") || "all";

  // Local controlled state for the search input so typing live-filters with a
  // small debounce. Keeps URL params clean for shareable filtered views.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => { setSearchInput(q); }, [q]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((searchInput || "") !== (q || "")) setParam("q", searchInput.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [searchInput]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === "all") next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status, category]);

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ limit: "300" });
      if (q) qs.set("q", q);
      if (status !== "all") qs.set("status", status);
      if (category !== "all") qs.set("category", category);
      const { data } = await api.get(`/operations/blog/posts?${qs.toString()}`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (post, e) => {
    e.stopPropagation();
    const next = post.status === "published" ? "draft" : "published";
    try {
      await api.post(`/operations/blog/posts/${post.slug}/status`, { status: next });
      toast.success(next === "published" ? "Published" : "Reverted to draft");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (post, e) => {
    e.stopPropagation();
    if (!window.confirm(`Permanently delete "${post.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/operations/blog/posts/${post.slug}`);
      toast.success("Post deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const duplicate = async (post, e) => {
    e.stopPropagation();
    try {
      const { data } = await api.post(`/operations/blog/posts/${post.slug}/duplicate`);
      toast.success("Duplicate created as draft");
      navigate(`/admin/blog/${encodeURIComponent(data.slug)}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const regenerateSitemap = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/operations/blog/regenerate-sitemap`);
      toast.success(`Sitemap regenerated — ${data.url_count} URLs (${Math.round(data.sitemap_bytes / 1024)} KB)`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRegenerating(false);
    }
  };

  const counts = useMemo(() => {
    const published = data.posts.filter((p) => p.status === "published").length;
    const drafts = data.posts.filter((p) => p.status === "draft").length;
    return { published, drafts };
  }, [data.posts]);

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="admin-blog-index">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Content</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Blog CMS</h1>
          <div className="text-sm text-[#6B7280] mt-1.5 flex flex-wrap items-baseline gap-x-2">
            <span><span className="font-medium text-[#0A2540] tabular-nums">{data.posts.length}</span> showing</span>
            <span>·</span>
            <span><span className="font-medium text-[#047857] tabular-nums">{counts.published}</span> published</span>
            <span>·</span>
            <span><span className="font-medium text-[#92400E] tabular-nums">{counts.drafts}</span> drafts</span>
            <span>·</span>
            <span><span className="font-medium text-[#0A2540] tabular-nums">{data.total}</span> total in library</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="h-9"
            onClick={regenerateSitemap}
            disabled={regenerating}
            data-testid="blog-regenerate-sitemap-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating ? "animate-spin" : ""}`} />
            {regenerating ? "Regenerating…" : "Regenerate sitemap"}
          </Button>
          <Link to="/admin/blog/new">
            <Button className="bg-[#0066FF] hover:bg-[#0052CC] h-9" data-testid="blog-new-post-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New post
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
          <Input
            placeholder="Search by title, slug, or meta description…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 h-9"
            data-testid="blog-search"
          />
        </div>
        <Select value={status} onValueChange={(v) => setParam("status", v)}>
          <SelectTrigger className="h-9 w-[160px]" data-testid="blog-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="draft">Drafts</SelectItem>
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={(v) => setParam("category", v)}>
          <SelectTrigger className="h-9 w-[200px]" data-testid="blog-category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(q || status !== "all" || category !== "all") && (
          <Button variant="ghost" className="h-9" onClick={() => setParams({})} data-testid="blog-clear-filters">
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-4 py-2.5 font-medium">Title</th>
                <th className="px-4 py-2.5 font-medium">Category</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium text-right">Words</th>
                <th className="px-4 py-2.5 font-medium">Published</th>
                <th className="px-4 py-2.5 font-medium">Updated</th>
                <th className="px-4 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="p-10 text-center text-[#6B7280]" data-testid="blog-loading">Loading posts…</td></tr>
              )}
              {!loading && data.posts.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-[#6B7280]" data-testid="blog-empty">
                  No posts match these filters. <Link to="/admin/blog/new" className="text-[#0066FF] hover:underline">Write the first one →</Link>
                </td></tr>
              )}
              {!loading && data.posts.map((p) => {
                const meta = STATUS_LABEL[p.status] || STATUS_LABEL.published;
                return (
                  <tr
                    key={p.slug}
                    data-testid={`blog-row-${p.slug}`}
                    onClick={() => navigate(`/admin/blog/${encodeURIComponent(p.slug)}`)}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5 text-[#9CA3AF] flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[#0A2540] font-medium truncate max-w-[460px]">{p.title}</div>
                          <div className="text-[11px] text-[#9CA3AF] truncate max-w-[460px]">/{p.slug}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[#374151]">{p.category || "—"}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wider rounded border ${meta.cls}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-[#6B7280]">{(p.word_count || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(p.published_at) || "—"}</td>
                    <td className="px-4 py-2.5 text-[#6B7280] tabular-nums">{formatDate(p.updated_at) || "—"}</td>
                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={(e) => togglePublish(p, e)}
                          title={p.status === "published" ? "Move to draft" : "Publish"}
                          data-testid={`blog-toggle-${p.slug}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded text-[#6B7280] hover:text-[#0A2540] hover:bg-[#F3F4F6]"
                        >
                          {p.status === "published" ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                        <Link
                          to={`/admin/blog/${encodeURIComponent(p.slug)}`}
                          title="Edit"
                          data-testid={`blog-edit-${p.slug}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded text-[#6B7280] hover:text-[#0066FF] hover:bg-[#EFF6FF]"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Link>
                        <button
                          onClick={(e) => duplicate(p, e)}
                          title="Duplicate as draft"
                          data-testid={`blog-duplicate-${p.slug}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded text-[#6B7280] hover:text-[#0066FF] hover:bg-[#EFF6FF]"
                        >
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <a
                          href={`/blog/${p.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Open public page"
                          data-testid={`blog-open-${p.slug}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded text-[#6B7280] hover:text-[#0066FF] hover:bg-[#EFF6FF]"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={(e) => remove(p, e)}
                          title="Delete"
                          data-testid={`blog-delete-${p.slug}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded text-[#9CA3AF] hover:text-[#B91C1C] hover:bg-[#FEF2F2]"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
